import { BooleanInput } from "@angular/cdk/coercion";
import { NgClass } from "@angular/common";
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from "@angular/router";
import { FuseFullscreenComponent } from "@fuse/components/fullscreen";
import { FuseLoadingBarComponent } from "@fuse/components/loading-bar";
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { STORAGE_KEY } from "app/app-constant";
import { CLIENT_NAVIGATION } from "app/client-route";
import { NavigationService } from "app/core/navigation/navigation.service";
import { Navigation } from "app/core/navigation/navigation.types";
import { UserService } from "app/core/user/user.service";
import { User } from "app/core/user/user.types";
import { LanguagesComponent } from "app/layout/common/languages/languages.component";
import { MessagesComponent } from "app/layout/common/messages/messages.component";
import { NotificationsComponent } from "app/layout/common/notifications/notifications.component";
import { QuickChatComponent } from "app/layout/common/quick-chat/quick-chat.component";
import { SearchComponent } from "app/layout/common/search/search.component";
import { ShortcutsComponent } from "app/layout/common/shortcuts/shortcuts.component";
import { UserComponent } from "app/layout/common/user/user.component";
import { IdleTimeoutService } from "app/service/idle-timeout.service";
import { AppName } from "environments/app-nam.enum";
import { environment } from "environments/environment";
import { jwtDecode } from "jwt-decode";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "classy-layout",
  templateUrl: "./classy.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    NotificationsComponent,
    UserComponent,
    MatIconModule,
    MatButtonModule,
    LanguagesComponent,
    FuseFullscreenComponent,
    SearchComponent,
    ShortcutsComponent,
    MessagesComponent,
    RouterOutlet,
    RouterLink,
    QuickChatComponent,
    NgClass,
    MatDividerModule,
    MatMenuModule,
  ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  user: User;
  navigationOpened: boolean = true;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  static ngAcceptInputType_showAvatar: BooleanInput;
  @Input() showAvatar: boolean = true;

  navigation: any;
  appLogo: string = "";
  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _userService: UserService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private idleTimeoutService: IdleTimeoutService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  private _setAppLogo() {
    switch (environment.APP_NAME) {
      case AppName.IHCL:
        this.appLogo = "images/logo/ihcl.png";
        break;

      case AppName.APBL:
        this.appLogo = "images/logo/apbl.svg";
        break;
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  companyLogo: string = "";
  ngOnInit(): void {
    // Subscribe to navigation data
    // this._navigationService.navigation$
    //   .pipe(takeUntil(this._unsubscribeAll))
    //   .subscribe((navigation: Navigation) => {
    //     debugger
    //     this.navigation = {default:defaultNavigation};
    //   });
    this._setAppLogo();
    const config = JSON.parse(localStorage.getItem(STORAGE_KEY.USER_CONFIG));
    this.companyLogo = config.companyLogo;
    this.navigation = this.filterNavigation(config);
    this.user = JSON.parse(localStorage.getItem(STORAGE_KEY.USER_INFO));
    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes("md");
        if (this.isScreenSmall) {
          this.navigationOpened = false;
        } else {
          this.navigationOpened = true;
        }
      });
  }

  filterNavigation(userInfo: any): any[] {
    if (!userInfo) {
      return [];
    }

    if (userInfo.userType === "admin" || userInfo.userType === "sub-admin") {
      return CLIENT_NAVIGATION;
    }

    if (userInfo.userType === "user" || userInfo.userType === "vendor") {
      return this.filterNavTree(
        CLIENT_NAVIGATION,
        userInfo?.roleDetails?.modularPermission
      );
    }
  }

  /**
   * Walk the grouped navigation tree and keep only items the user has permission for.
   * Group/section nodes (no `permission`) are kept if any descendant survives, dropped otherwise.
   */
  private filterNavTree(items: any[], permissions: string[]): any[] {
    return items
      .map((item) => {
        const hasChildren = item.children?.length > 0;
        // Container/group node: filter children first, drop if all hidden.
        if (!item.permission && hasChildren) {
          const children = this.filterNavTree(item.children, permissions);
          return children.length ? { ...item, children } : null;
        }
        // Collapsable/leaf with permission: keep if user has it (and recurse into children if present).
        if (item.permission) {
          if (!this.hasPermission(item.permission, permissions)) return null;
          if (hasChildren) {
            const children = this.filterNavTree(item.children, permissions);
            return { ...item, children };
          }
          return item;
        }
        // Other types (divider/spacer): keep as-is.
        return item;
      })
      .filter(Boolean);
  }

  hasPermission(permission: string, permissionsList: string[]): boolean {
    return permissionsList.includes(permission); // Check if the permission is in the user's list of permissions
  }

  decodedToken: any;
  getToken() {
    const token = localStorage.getItem(STORAGE_KEY.TOKEN);
    if (!token) {
      console.error("No token found in localStorage");
      return null;
    }

    try {
      const decodedToken = jwtDecode(token);
      this.decodedToken = decodedToken; // Access payload, e.g., modularPermissions
      return this.decodedToken;
    } catch (error) {
      console.error("Failed to decode token:", error.message);
    }
  }
  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
      this.navigationOpened = !this.navigationOpened;
    }
  }

  goToDashboard(): void {
    this._router.navigate(["/dashboard"]);
  }

  signOut(): void {
    this._router.navigate(["/sign-out"]);
  }
}
